import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { Avatar, Box, Button } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { ClickAwayListener } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useTheme, useMediaQuery } from "@mui/material";

interface WeekChooserInNavParams {
    setSelectedWeek: React.Dispatch<React.SetStateAction<number | undefined>>;
    setFirstDayOfWeek: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const WeekChooserInNav = ({
    setSelectedWeek,
    setFirstDayOfWeek,
}: WeekChooserInNavParams) => {
    const defaultDate = new Date();
    const [value, setValue] = useState<Dayjs | null>(dayjs(defaultDate));
    const [open, setOpen] = useState(false);
    const [selectedWeekNumber, setSelectedWeekNumber] = useState<number>();
    const [daysRange, setDaysRange] = useState({
        firstDay: "",
        lastDay: "",
    });
    const [isWide, setIsWide] = useState(window.innerWidth > 600);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        const handleResize = () => {
            setIsWide(window.innerWidth > 600);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const handleClick = () => {
        setOpen((prev) => !prev);
    };

    const handleClickAway = () => {
        setOpen(false);
    };

    const whatWeekIsIt = () => {
        const today = value?.toDate(),
            janFirst = new Date(today!.getFullYear(), 0, 1);

        return Math.ceil(
            ((Number(today) - Number(janFirst)) / 86400000 +
                janFirst.getDay() +
                1) /
                7,
        );
    };

    useEffect(() => {
        if (value) {
            setSelectedWeekNumber(whatWeekIsIt());
            setSelectedWeek(whatWeekIsIt());
        }
    }, [value]);

    useEffect(() => {
        if (
            typeof selectedWeekNumber != "undefined" &&
            !isNaN(selectedWeekNumber)
        ) {
            let newFullFirstDay: any;
            let newFirstDay: any;
            let newLastDay: any;

            const weekRange = (weekNum: number) => {
                const today = value?.toDate(),
                    janFirst = new Date(today!.getFullYear(), 0, 1);
                const baseDate = dayjs(janFirst);

                // Adjust the base date to Monday
                const daysToSubtract =
                    baseDate.day() === 0 ? 6 : baseDate.day() - 1; // Sunday = 0, Monday = 1, ...
                const mondayFirstWeek = baseDate.subtract(
                    daysToSubtract,
                    "day",
                );

                newFullFirstDay = mondayFirstWeek
                    .add((weekNum! - 1) * 7, "day")
                    .format("YYYY-MM-DD")
                    .toString();

                newFirstDay = mondayFirstWeek
                    .add((weekNum! - 1) * 7, "day")
                    .format("DD MMM")
                    .toString();

                newLastDay = mondayFirstWeek
                    .add(weekNum! * 7 - 1, "day")
                    .format("DD MMM, YYYY")
                    .toString();

                return { newLastDay, newFirstDay };
            };
            weekRange(selectedWeekNumber);

            setDaysRange({
                ...daysRange,
                firstDay: newFirstDay,
                lastDay: newLastDay,
            });
            setFirstDayOfWeek(newFullFirstDay);
        }
    }, [selectedWeekNumber]);

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div
                style={{
                    position: "relative",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                            onClick={() => [
                                setSelectedWeekNumber((prev) => prev! - 1),
                                setSelectedWeek((prev) => prev! - 1),
                            ]}
                            sx={{
                                backgroundColor: "background.paper",
                                color: "primary.main",
                                "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "primary.contrastText",
                                    cursor: "pointer",
                                },
                                width: 32,
                                height: 32,
                            }}
                        >
                            <ArrowBackIosNewIcon sx={{ fontSize: "medium" }} />
                        </Avatar>
                        <Box
                            sx={{
                                display: isMobile ? "flex" : "block",
                                flexDirection: "column",
                            }}
                        >
                            <span>{daysRange.firstDay} - </span>
                            <span>{daysRange.lastDay}</span>
                        </Box>
                        <Avatar
                            onClick={() => [
                                setSelectedWeekNumber((prev) => prev! + 1),
                                setSelectedWeek((prev) => prev! + 1),
                            ]}
                            sx={{
                                backgroundColor: "background.paper",
                                color: "primary.main",
                                "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "primary.contrastText",
                                    cursor: "pointer",
                                },
                                width: 32,
                                height: 32,
                            }}
                        >
                            <ArrowForwardIosIcon sx={{ fontSize: "medium" }} />
                        </Avatar>
                    </Box>
                    {isWide ? (
                        <Button
                            variant="contained"
                            endIcon={<ArrowDropDownIcon />}
                            sx={{
                                backgroundColor: "background.paper",
                                color: "primary.main",
                                "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "primary.contrastText",
                                },
                            }}
                            onClick={() => handleClick()}
                        >
                            Week
                        </Button>
                    ) : (
                        ""
                    )}
                </Box>
                {open && (
                    <Box
                        sx={{
                            position: "absolute",
                            right: 0,
                            backgroundColor: "background.default",
                            borderRadius: 1,
                            mt: 2,
                            boxShadow: 3,
                        }}
                    >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateCalendar
                                sx={{
                                    "& .MuiPickersDay-root": {
                                        height: "1.5rem",
                                        width: "1.5rem",
                                    },
                                    "& .MuiPickersDay-root.Mui-selected": {
                                        backgroundColor: "#338CCD",
                                        color: "#FCFFFD",
                                    },
                                    "& .MuiPickersDay-root.Mui-selected:hover":
                                        {
                                            backgroundColor: "#98CBFD",
                                        },
                                    "& .MuiPickersDay-root:focus.Mui-selected":
                                        {
                                            backgroundColor: "#338CCD",
                                            color: "#FCFFFD",
                                        },
                                }}
                                value={value}
                                onChange={(newValue) => setValue(newValue)}
                            />
                        </LocalizationProvider>
                    </Box>
                )}
            </div>
        </ClickAwayListener>
    );
};

export default WeekChooserInNav;
